import React from 'react';
import { get, concat, toInteger } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { toDoItemDetailActivityWidget as ToDoItemDetailActivityWidgetView } from 'ui/views/widgets/toDoItemDetailActivityWidget';
import { ToDoItemDetailActivityWidgetService } from 'services/toDoItemDetailActivityWidget';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ToDoItemDetailActivityWidget } from 'ui/containers/ToDoItemDetailActivityWidget';
 *
 * // render
 * <ToDoItemDetailActivityWidget propName='propValue'/>
 */
export class _ToDoItemDetailActivityWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            additionalCommentValue: '',
            additionalCommentActionDisabled: true,
            notesValue: '',
            notesActionDisabled: true,

            /*
            there was a change in the requirements, thus pagination has been disabled
            **/

            disablePagination: true,
            pagination: {}
        };

        // bind functions
        this.onChange = this.onChange.bind( this );
        this.onPost = this.onPost.bind( this );
        this.handlePagination = this.handlePagination.bind( this );

        // create service instance
        this.toDoItemDetailActivityWidgetService = new ToDoItemDetailActivityWidgetService();

        // start data fetch
        this.fetchData();
    }

    /**
     * @desc fetch component data
     */
    async fetchData() {
        log.debug( 'ToDoItemDetailActivityWidget.fetchData()' );

        // cancel ongoing request
        this.toDoItemDetailActivityWidgetService.cancel();

        // get status and service data
        const { status, data } = await this.toDoItemDetailActivityWidgetService.fetchData( {
            url: this.props.widget.url,
            data: {
                ...this.props.requestPayload
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                pagination: data.structure.paginationConfig.pagination
            } );
        }
    }

    async handlePagination() {
        log.debug( 'ToDoItemDetailActivityWidget.handlePagination()' );

        // if request is already in progress or pagination disabled, ignore pagination
        if( this.state.isFetching || this.state.disablePagination ) {
            return;
        }

        const url = get( this.state.componentData, 'structure.paginationConfig.url', {} );
        const componentData = this.state.componentData;
        const nextStartIndex = toInteger( get( this.state, 'pagination.startIndex', 0 ) ) + toInteger( get( this.state, 'pagination.chunkSize', 0 ) );
        const newPagination = this.state.pagination;
        newPagination.startIndex = nextStartIndex;

        this.setState( {
            pagination: newPagination
        } );

        const { status, data } = await this.toDoItemDetailActivityWidgetService.fetchData( {
            url: url,
            data: {
                ...this.props.requestPayload,
                data: this.state.pagination
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            const newItems = get( data, 'data.state.items', [] );
            const appendedItems = concat( componentData.data.state.items,  newItems );
            componentData.data.state.items = appendedItems;

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: componentData,
                disablePagination: ( newItems.length < this.state.pagination.chunkSize ) ? true : false
            } );
        }
        
    }

    /**
     * @desc function to handle changes in textareas
     */
    async onChange ( { name, value } ) {
        log.debug( 'ToDoItemDetailActivityWidget.onChange()' );

        if ( 'additionalComment' === name ) {
            this.setState( {
                additionalCommentValue: value,
                additionalCommentActionDisabled: ( value.length ) ? false : true
            } );
        } else if ( 'notes' === name ) {
            this.setState( {
                notesValue: value,
                notesActionDisabled: ( value.length ) ? false : true
            } );
        }
    }

    /**
     * @desc function to handle comment/notes submission
     */
    async onPost( { name, url } ) {

        // show page preloader
        pageLevelPreloader.show();

        let data = {};
        const context = get( this.props, 'requestPayload.context', {} );

        if ( 'additionalComment' === name ) {
            data = {
                additionalComment: this.state.additionalCommentValue
            };
        } else if ( 'notes' === name ) {
            data = {
                notes: this.state.notesValue
            };
        }

        const { status } = await this.toDoItemDetailActivityWidgetService.onPost( {
            url: url,
            data: {
                context,
                data
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                additionalCommentValue: '',
                additionalCommentActionDisabled: true,
                notesValue: '',
                notesActionDisabled: true,
                isFetching: true
            }, () => {

                successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ), } );
                this.fetchData();
            } );
        } else {
            errorOverlay.open();
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <ToDoItemDetailActivityWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                {...get( this.state, 'componentData', {} )}
                additionalCommentValue={ this.state.additionalCommentValue }
                additionalCommentActionDisabled = { this.state.additionalCommentActionDisabled }
                notesValue={ this.state.notesValue }
                notesActionDisabled={ this.state.notesActionDisabled }
                onChange={ this.onChange }
                onPost= {this.onPost }
                onPagination={ this.handlePagination }
                disablePagination={ this.state.disablePagination }
            />
        );
    }
}

// set display name
_ToDoItemDetailActivityWidget.displayName = 'ToDoItemDetailActivityWidget';

// set default props
_ToDoItemDetailActivityWidget.defaultProps = {};

// prop types
_ToDoItemDetailActivityWidget.propTypes = {};

export const ToDoItemDetailActivityWidget = withRouter( _ToDoItemDetailActivityWidget );
